import { useState } from 'react'
import { Copy, Check } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

/**
 * Small inline copy-to-clipboard button. Renders nothing when there's no value,
 * so it can be dropped next to any optional field (IMEI/IMSI/ICCID/firmware…).
 * Stops click propagation so it never triggers a surrounding clickable card.
 */
export function CopyButton({
  value,
  label,
  className,
}: {
  value?: string | null
  label?: string
  className?: string
}) {
  const { t } = useTranslation('common')
  const [copied, setCopied] = useState(false)

  if (!value) return null

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      toast.success(t('actions.copied', { defaultValue: label ? `${label} copied` : 'Copied' }))
      window.setTimeout(() => setCopied(false), 1200)
    } catch {
      toast.error(t('actions.copyFailed', { defaultValue: 'Copy failed' }))
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={t('actions.copyValue', { defaultValue: label ? `Copy ${label}` : 'Copy' })}
      title={t('actions.copy', { defaultValue: 'Copy' })}
      className={cn(
        'shrink-0 inline-flex items-center justify-center rounded p-0.5',
        'text-muted-foreground/50 hover:text-foreground hover:bg-accent transition-colors',
        className,
      )}
    >
      {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
    </button>
  )
}
